import React from 'react';
import { Container, Typography, Grid, Box } from '@mui/material';
import { Link } from 'react-router-dom';
import './LiftLog.scss';
import screenHome from './images/home.png';
import screenProgress from './images/progress.png';
import screenCharts from './images/charts.png';
import screenWorkout from './images/workout.png';
import screenExercises from './images/exercises.png';
import screenTemplates from './images/templates.png';

const features = [
    {
        icon: '🏋️',
        title: 'Track Every Rep',
        desc: 'Log sets, reps, and weight for every exercise. Track your workout history and never lose progress.',
    },
    {
        icon: '📊',
        title: 'Progress Analytics',
        desc: 'Visualize your strength gains with charts for weight, volume, and estimated 1RM over time.',
    },
    {
        icon: '💪',
        title: 'Muscle Insights',
        desc: 'See which muscle groups you train most and least. Balance your routine for optimal results.',
    },
    {
        icon: '📋',
        title: 'Workout Templates',
        desc: 'Create and save custom workout templates. Start your sessions faster with pre-built routines.',
    },
    {
        icon: '🏆',
        title: 'Personal Records',
        desc: 'Automatically track PRs for best weight, best set, best volume, and estimated 1RM.',
    },
    {
        icon: '🔍',
        title: 'Exercise Library',
        desc: 'Browse exercises by muscle group — chest, back, shoulders, legs, arms, core, and more.',
    },
];

const screenshots = [
    { src: screenHome, alt: 'LiftLog home screen with workout templates and streak tracking' },
    { src: screenProgress, alt: 'LiftLog progress tracking dashboard showing weekly workouts, volume, and PRs' },
    { src: screenCharts, alt: 'LiftLog exercise progress charts with weight and volume tracking over time' },
    { src: screenWorkout, alt: 'LiftLog active workout screen with set, rep, and weight logging' },
    { src: screenExercises, alt: 'LiftLog exercise library with muscle group filters' },
    { src: screenTemplates, alt: 'LiftLog workout template builder with exercise list' },
];

const LiftLog = () => (
    <div className="liftlog-landing">
        {/* Hero */}
        <section className="liftlog-hero">
            <Container maxWidth="md">
                <span className="liftlog-badge">iOS Fitness App</span>
                <Typography variant="h2" className="liftlog-hero-title">
                    Your Gym Journal,{' '}
                    <span className="accent">Reimagined</span>
                </Typography>
                <Typography className="liftlog-hero-sub">
                    LiftLog is the simplest way to track your workouts, monitor progress, and hit new personal records.
                    Built for lifters who want results, not complexity.
                </Typography>
                <a
                    href="https://apps.apple.com/app/liftlog"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <img
                        src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                        alt="Download LiftLog on the App Store"
                        className="app-store-badge"
                    />
                </a>
            </Container>
        </section>

        {/* Screenshots */}
        <section className="liftlog-screens">
            <Container maxWidth="lg">
                <Typography variant="h5" className="screens-title">
                    See It in Action
                </Typography>
                <div className="screens-scroll">
                    {screenshots.map((screen, i) => (
                        <div className="screen-item" key={i}>
                            <img src={screen.src} alt={screen.alt} loading="lazy" />
                        </div>
                    ))}
                </div>
            </Container>
        </section>

        {/* Features */}
        <section className="liftlog-features">
            <Container maxWidth="lg">
                <Typography variant="h5" className="features-title">
                    Everything You Need to Get Stronger
                </Typography>
                <Typography className="features-sub">
                    Simple, powerful tools designed for serious lifters.
                </Typography>
                <Grid container spacing={3}>
                    {features.map((f, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                            <div className="feature-card">
                                <span className="feature-icon">{f.icon}</span>
                                <Typography variant="h6" className="feature-title">
                                    {f.title}
                                </Typography>
                                <Typography className="feature-desc">
                                    {f.desc}
                                </Typography>
                            </div>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </section>

        {/* CTA */}
        <section className="liftlog-cta">
            <Container maxWidth="sm">
                <Typography variant="h5" className="cta-title">
                    Start Tracking Your Gains Today
                </Typography>
                <Typography className="cta-sub">
                    Free to download. No account required to start lifting.
                </Typography>
                <a
                    href="https://apps.apple.com/app/liftlog"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <img
                        src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                        alt="Download LiftLog on the App Store"
                        className="app-store-badge"
                    />
                </a>
            </Container>
        </section>

        {/* Footer */}
        <footer className="liftlog-footer">
            <Container>
                <Box>
                    <Link to="/liftlog/privacy">Privacy Policy</Link>
                    <Link to="/liftlog/terms">Terms &amp; Conditions</Link>
                    <Link to="/liftlog/support">Support</Link>
                </Box>
                <Typography className="footer-copy">
                    &copy; {new Date().getFullYear()} Evolve Labs. All rights reserved.
                </Typography>
            </Container>
        </footer>
    </div>
);

export default LiftLog;
